import { ComponentType } from "react";
import { MenuItem } from "primereact/menuitem";

// Route Types
export interface RouteConfig {
  path: string;
  element: ComponentType<any>;
  children?: RouteConfig[];
  protected?: boolean;
  adminOnly?: boolean;
  platformsOnly?: boolean;
}

export interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  heading?: string;
  requiredRole?: string;
  requiredTool?: string;
  children?: NavigationItem[];
}

// User Role Types
export interface UserRoles {
  role?: string;
  tools?: string[]; // Array of tool names that user has access to
}

// Centralized Route Configuration
export const ROUTES = {
  // Public Routes
  LOGIN: "/login",
  ROOT: "/",
  TEST: "/test",

  // Protected Routes
  HOMEPAGE: "/homepage",

  //Youtube network health check
  YOUTUBE_SCORECARD: "/youtube_scorecard",
  YOUTUBE_SCORECARD_METADATA: "/youtube_scorecard/metadata",
  YOUTUBE_SCORECARD_CLAIMS_ADS: "/youtube_scorecard/claims_ads",
  YOUTUBE_SCORECARD_WHITELIST: "/youtube_scorecard/whitelist",
  YOUTUBE_SCORECARD_MOONBUG_MUSIC_LABEL:
    "/youtube_scorecard/moonbug_music_label",
  YOUTUBE_SCORECARD_CUSTOM_ID_LOOKUP:
    "/youtube_scorecard/custom_id_lookup",
  YOUTUBE_SCORECARD_REFERENCE_STATUS:
    "/youtube_scorecard/reference_status",

  // Music Console (new grouping)
  MUSIC_CONSOLE: "/music_console",

  // Music Asset Audits (now under musicConsole)
  // DATA_HEALTH_TOOL: "/data_health_tool",
  // SYNC_OWNERSHIP: "/data_health_tool/sync_ownership",
  // MUSIC_VIDEOS: "/data_health_tool/music_videos",
  // STUB_ASSETS: "/data_health_tool/stub_assets",
  // ASSET_TOOL: "/data_health_tool/asset_tool",
  // INCORRECT_ASSET_TYPES: "/data_health_tool/asset_tool/incorrect_asset_types",
  // MISMATCHED_SOUND_RECORDINGS: "/data_health_tool/mismatched_sound_recordings",

  // YouTube Music Asset Audits (new from notes)
  YOUTUBE_MUSIC_ASSET_AUDITS: "/youtube_music_asset_audits",
  YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD: "/youtube_music_asset_audits/dashboard",
  MUSIC_LABEL_DASHBOARD: "/youtube_music_asset_audits/music_label_dashboard",
  /** Standalone base path; nav item stays under YouTube Music group */
  CLAIM_POLICY_PATCH_DASHBOARD: "/youtube_music/claim_policy_patch_dashboard",
  CLAIM_POLICY_PATCH_DASHBOARD_OVERVIEW:
    "/youtube_music/claim_policy_patch_dashboard/overview",
  CLAIM_POLICY_PATCH_DASHBOARD_UPDATED_CLAIMS:
    "/youtube_music/claim_policy_patch_dashboard/updated_claims",
  MUSIC_VIDEO_EMBEDDING: "/youtube_music_asset_audits/music_video_embedding",
  MUSIC_VIDEO_METADATA: "/youtube_music_asset_audits/music_video_metadata",
  ASSET_TYPE_AUDIT: "/youtube_music_asset_audits/asset_type_audit",
  STUB_ASSET_AUDIT: "/youtube_music_asset_audits/stub_asset_audit",
  MISMATCHED_SOUND_RECORDINGS:
    "/youtube_music_asset_audits/mismatched_sound_recordings",
  METADATA_MISMATCH_AUDIT:
    "/youtube_music_asset_audits/metadata_mismatch_audit",
  MV_METADATA_GAPS: "/youtube_music_asset_audits/mv_metadata_gaps",
  BRAND_ARTIST_LOOKUP: "/youtube_music_asset_audits/brand_artist_lookup",

  // Music CMS Tools (new from notes)
  MUSIC_CMS_TOOLS: "/music_cms_tools",
  SOUND_RECORDING_EMBEDDER: "/music_cms_tools/sound_recording_embedder",

  // Composition Assets (new from notes)
  COMPOSITION_ASSETS: "/composition_assets",
  UMPG_SYNC_AUDIT: "/composition_assets/umpg_sync_audit",
  COMPOSITION_GENERAL_INSPECTION: "/composition_assets/general_inspection",
  COMPOSITION_METADATA_AUDIT_COMPARER: "/composition_assets/metadata_audit_comparer",

  SCRAPERS: "/scrapers",
  MUSIC_ASSET_SCRAPER: "/scrapers/music_asset_scraper",
  MUSIC_ASSET_SCRAPER_EXPLORER: "/scrapers/music_asset_scraper/explorer",

  // DSR Audits (now under musicConsole)
  DSR_AUDITS: "/dsr_audits",
  DSR_GENENT: "/dsr_audits/genent",
  DSR_MUSIC_VIDEOS: "/dsr_audits/music_videos",
  DSR_SOUND_RECORDINGS: "/dsr_audits/sound_recordings",
  DSR_MASTER_ARTISTS: "/dsr_audits/master_artists",
  DSR_SFTP: "/dsr_audits/sftp",

  // Sound Recording Linking Tool (now under musicConsole)
  SOUND_RECORDINGS: "/sound_recordings",
  SOUND_RECORDINGS_LINKING_TOOL: "/sound_recordings/linking_tool",
  SOUND_RECORDINGS_LINKING_TABLE_VIEW: "/sound_recordings/table_view",

  // YouTube Tools (remaining separate)
  BANNER_SWAP: "/banner_swap",
  BANNER_SWAP_CHANNEL_BANNERS: "/banner_swap/channel_banners",
  BANNER_SWAP_SWAP_QUEUE: "/banner_swap/swap_queue",
  /** V2 banner dashboard (automated swap + granular outcomes). Legacy tool: BANNER_SWAP */
  BANNER_DASHBOARD: "/banner_dashboard",
  BANNER_DASHBOARD_DEFAULT_BANNERS: "/banner_dashboard/default_banners",
  BANNER_DASHBOARD_SWAP_QUEUE: "/banner_dashboard/swap_queue",
  BANNER_DASHBOARD_CALENDAR: "/banner_dashboard/calendar",
  // Classifiers (Quality Auditor + Music Classifier)
  CLASSIFIERS: "/classifiers",
  CLASSIFIERS_MUSIC_CLASSIFIER: "/classifiers/music_classifier",
  CLASSIFIERS_MUSIC_CLASSIFIER_HISTORY: "/classifiers/music_classifier_history",
  CLASSIFIERS_VIDEO_AUDITOR: "/classifiers/video_auditor",
  CLASSIFIERS_BULK: "/classifiers/bulk",

  /** Top-level metadata audits (title QC, future tabs) */
  METADATA_AUDIT: "/metadata_audit",
  METADATA_AUDIT_TITLE_QC: "/metadata_audit/title_qc",
  METADATA_AUDIT_DICTIONARY: "/metadata_audit/dictionary",
  METADATA_AUDIT_ADMIN: "/metadata_audit/admin",

  // Composition Metadata Audit Comparer (now under composition assets)
  // COMPOSITION_METADATA_AUDIT_COMPARER: "/composition_metadata_audit/comparer",

  // OAC Audit
  OAC_AUDIT: "/oac_audit",
  OAC_AUDIT_CHANNEL_SETTINGS: "/oac_audit/channel_settings",
  OAC_AUDIT_PLAYLISTS: "/oac_audit/playlists",

  // Channel Checkup
  CHANNEL_CHECKUP: "/channel_checkup",

  // Other Tools
  LABELS_DASHBOARD: "/labels_dashboard",
  SPOTIFY_PODCAST_UPLOADER: "/spotify_podcast_uploader",

  // Admin Routes
  ADMIN_TOOLS: "/admin_tools",
  BANNER_SWAP_ADMIN_OPS: "/admin/banner_swap_ops",

  // Error Routes
  NOT_FOUND: "*",
} as const;

// Navigation Configuration
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    label: "Home",
    path: ROUTES.HOMEPAGE,
    icon: "pi pi-home",
    heading: "Main",
  },
  {
    label: "Network Health Check",
    path: ROUTES.YOUTUBE_SCORECARD,
    icon: "pi pi-check-circle",
    requiredTool: "YouTubeScorecard",
    children: [
      // {
      //   label: "Dashboard",
      //   path: ROUTES.YOUTUBE_SCORECARD,
      //   icon: "pi pi-chart-bar",
      //   requiredTool: "YouTubeScorecard",
      // },
      {
        label: "Metadata & Properties",
        path: ROUTES.YOUTUBE_SCORECARD_METADATA,
        icon: "pi pi-tags",
        requiredTool: "YouTubeScorecard",
      },
      {
        label: "Claims & Ads",
        path: ROUTES.YOUTUBE_SCORECARD_CLAIMS_ADS,
        icon: "pi pi-dollar",
        requiredTool: "YouTubeScorecard",
      },
      {
        label: "Moonbug Music Label",
        path: ROUTES.YOUTUBE_SCORECARD_MOONBUG_MUSIC_LABEL,
        icon: "pi pi-star",
        requiredTool: "YouTubeScorecard",
      },
      // {
      //   label: "Whitelist",
      //   path: ROUTES.YOUTUBE_SCORECARD_WHITELIST,
      //   icon: "pi pi-thumbs-up",
      //   requiredTool: "YouTubeScorecard",
      // This should probably be conditioned on the user role/email like in the component
      // but for now I will add it, and it can be filtered later.
      // },
      {
        label: "Custom ID Lookup",
        path: ROUTES.YOUTUBE_SCORECARD_CUSTOM_ID_LOOKUP,
        icon: "pi pi-search-plus",
        requiredTool: "YouTubeCustomIdLookup",
      },
      {
        label: "Reference Status",
        path: ROUTES.YOUTUBE_SCORECARD_REFERENCE_STATUS,
        icon: "pi pi-info-circle",
        requiredTool: "YouTubeReferenceStatus",
      },
    ],
  },
 
      {
        heading: "YouTube Music",
        label: "YouTube Music Assets",
        path: ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS,
        icon: "pi pi-youtube",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "Dashboard",
            path: ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD,
            icon: "pi pi-home",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Music Label Dashboard",
            path: ROUTES.MUSIC_LABEL_DASHBOARD,
            icon: "pi pi-star",
            requiredTool: "DataHealthTool",
          },
     
          {
            label: "Music Video Embedding Audit",
            path: ROUTES.MUSIC_VIDEO_EMBEDDING,
            icon: "pi pi-video",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Music Video Metadata Audit",
            path: ROUTES.MUSIC_VIDEO_METADATA,
            icon: "pi pi-tags",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Asset Type Audit",
            path: ROUTES.ASSET_TYPE_AUDIT,
            icon: "pi pi-check-circle",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Stub Asset Audit",
            path: ROUTES.STUB_ASSET_AUDIT,
            icon: "pi pi-file-excel",
            requiredTool: "DataHealthTool",
          },
          {
            label: "SoundRecording Mismatch Audit",
            path: ROUTES.MISMATCHED_SOUND_RECORDINGS,
            icon: "pi pi-exclamation-triangle",
            requiredTool: "DataHealthTool",
          },
          {
            label: "MV Metadata Gaps",
            path: ROUTES.MV_METADATA_GAPS,
            icon: "pi pi-pencil",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Brand → Artist Lookup",
            path: ROUTES.BRAND_ARTIST_LOOKUP,
            icon: "pi pi-list",
            requiredTool: "DataHealthTool",
          },
        ],
      },
      //dsr audits
      {
        label: "Music DSR",
        path: ROUTES.DSR_AUDITS,
        icon: "pi pi-file-edit",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "GenEnt Reports",
            path: ROUTES.DSR_GENENT,
            icon: "pi pi-palette",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Music Videos Reports",
            path: ROUTES.DSR_MUSIC_VIDEOS,
            icon: "pi pi-video",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Sound Recordings Reports",
            path: ROUTES.DSR_SOUND_RECORDINGS,
            icon: "pi pi-microphone",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Master Artists",
            path: ROUTES.DSR_MASTER_ARTISTS,
            icon: "pi pi-users",
            requiredTool: "DataHealthTool",
          },
          {
            label: "BMAT SFTP",
            path: ROUTES.DSR_SFTP,
            icon: "pi pi-upload",
            requiredTool: "DataHealthTool",
          },
        ],
      },

      {
        label: "Sound Recordings",
        path: ROUTES.SOUND_RECORDINGS,
        icon: "pi pi-wrench",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "Sound Recording Embedder",
            path: ROUTES.SOUND_RECORDINGS_LINKING_TOOL,
            icon: "pi pi-link",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Sound Recording Linking Table View",
            path: ROUTES.SOUND_RECORDINGS_LINKING_TABLE_VIEW,
            icon: "pi pi-link",
            requiredTool: "DataHealthTool",
          },
        ],
      },
      {
        label: "Composition Assets",
        path: ROUTES.COMPOSITION_ASSETS,
        icon: "pi pi-volume-up",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "UMPG SYNC Audit",
            path: ROUTES.UMPG_SYNC_AUDIT,
            icon: "pi pi-sync",
            requiredTool: "DataHealthTool",
          },
          {
            label: "General Inspection",
            path: ROUTES.COMPOSITION_GENERAL_INSPECTION,
            icon: "pi pi-search",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Composition Metadata Audit",
            path: ROUTES.COMPOSITION_METADATA_AUDIT_COMPARER,
            icon: "pi pi-list",
            requiredTool: "DataHealthTool",
          },
        ],
      },
      {
        label: "Claim Policy Patch Dashboard",
        path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD_OVERVIEW,
        icon: "pi pi-chart-line",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "Dashboard",
            path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD_OVERVIEW,
            icon: "pi pi-chart-line",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Updated Claims",
            path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD_UPDATED_CLAIMS,
            icon: "pi pi-list",
            requiredTool: "DataHealthTool",
          },
        ],
      },
      {
        label: "Scrapers",
        path: ROUTES.SCRAPERS,
        icon: "pi pi-search",
        requiredTool: "DataHealthTool",
        children: [
          {
            label: "Music Asset Scraper",
            path: ROUTES.MUSIC_ASSET_SCRAPER,
            icon: "pi pi-microphone",
            requiredTool: "DataHealthTool",
          },
          {
            label: "Music Asset Explorer",
            path: ROUTES.MUSIC_ASSET_SCRAPER_EXPLORER,
            icon: "pi pi-database",
            requiredTool: "DataHealthTool",
          },
        ],
      },

  {
    label: "Banner Dashboard",
    path: ROUTES.BANNER_DASHBOARD,
    icon: "pi pi-table",
    heading: "YouTube Channel Management Tools",
    requiredTool: "BannerSwap",
    children: [
      {
        label: "Default banners",
        path: ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS,
        icon: "pi pi-images",
        requiredTool: "BannerSwap",
      },
      {
        label: "Swap queue",
        path: ROUTES.BANNER_DASHBOARD_SWAP_QUEUE,
        icon: "pi pi-list",
        requiredTool: "BannerSwap",
      },
      {
        label: "Calendar",
        path: ROUTES.BANNER_DASHBOARD_CALENDAR,
        icon: "pi pi-calendar",
        requiredTool: "BannerSwap",
      },
    ],
  },
  {
    label: "Classifiers",
    path: ROUTES.CLASSIFIERS,
    icon: "pi pi-th-large",
    heading: "Content Classification",
    requiredTool: "Classifiers",
    children: [
      {
        label: "Music Classifier",
        path: ROUTES.CLASSIFIERS_MUSIC_CLASSIFIER,
        icon: "pi pi-volume-up",
        requiredTool: "Classifiers",
      },
      {
        label: "Music Classifier History",
        path: ROUTES.CLASSIFIERS_MUSIC_CLASSIFIER_HISTORY,
        icon: "pi pi-history",
        requiredTool: "Classifiers",
      },
      {
        label: "Video Auditor",
        path: ROUTES.CLASSIFIERS_VIDEO_AUDITOR,
        icon: "pi pi-video",
        requiredTool: "Classifiers",
      },
      {
        label: "Bulk Analysis",
        path: ROUTES.CLASSIFIERS_BULK,
        icon: "pi pi-list",
        requiredTool: "Classifiers",
      },
    ],
  },
  {
    label: "Metadata Audit",
    path: ROUTES.METADATA_AUDIT,
    icon: "pi pi-check-square",
    heading: "Content Classification",
    requiredTool: "MetadataAudit",
    children: [
      {
        label: "Title QC",
        path: ROUTES.METADATA_AUDIT_TITLE_QC,
        icon: "pi pi-file-edit",
        requiredTool: "MetadataAudit",
      },
      {
        label: "Dictionary Settings",
        path: ROUTES.METADATA_AUDIT_DICTIONARY,
        icon: "pi pi-book",
        requiredTool: "MetadataAudit",
      },
      {
        label: "Admin",
        path: ROUTES.METADATA_AUDIT_ADMIN,
        icon: "pi pi-cog",
        requiredTool: "MetadataAudit",
        requiredRole: "admin",
      },
    ],
  },
  {
    label: "OAC Audit",
    path: ROUTES.OAC_AUDIT,
    icon: "pi pi-check-square",
    heading: "YouTube Channel Management Tools",
    requiredTool: "OACAudit",
  },
  {
    label: "Channel Checkup",
    path: ROUTES.CHANNEL_CHECKUP,
    icon: "pi pi-heart",
    heading: "YouTube Channel Management Tools",
    requiredTool: "ChannelCheckup",
  },
  {
    label: "Labels Dashboard",
    path: ROUTES.LABELS_DASHBOARD,
    icon: "pi pi-book",
    requiredTool: "LabelsDashboard",
  },
  {
    label: "Spotify Podcast Uploader",
    path: ROUTES.SPOTIFY_PODCAST_UPLOADER,
    icon: "pi pi-megaphone",
    heading: "Spotify Tools",
    requiredTool: "spotifyUploader",
  },
  {
    label: "Admin Tools",
    path: ROUTES.ADMIN_TOOLS,
    icon: "pi pi-spin pi-cog",
    heading: "Admin Settings",
    requiredRole: "admin",
  },
  {
    label: "Banner Swap Ops",
    path: ROUTES.BANNER_SWAP_ADMIN_OPS,
    icon: "pi pi-wrench",
    requiredRole: "admin",
  },
];

// Custom MenuItem type that includes heading
export interface MenuItemWithHeading extends MenuItem {
  heading?: string;
}

// Utility Functions
export const hasAccess = (
  item: NavigationItem,
  userRoles: UserRoles
): boolean => {
  

  // Ensure userRoles has valid structure
  if (!userRoles) {
    return false;
  }

  // Tool check is always enforced, even for admins — tool access must be explicitly granted
  if (item.requiredTool) {
    const userTools = Array.isArray(userRoles.tools) ? userRoles.tools : [];

    // Special case: "user" role gets Network Health Check access without explicit tool assignment
    const isYoutubeScorecardDefault =
      userRoles.role === "user" && item.requiredTool === "YouTubeScorecard";

    if (!isYoutubeScorecardDefault && !userTools.includes(item.requiredTool)) {
      return false;
    }
  }

  // Admin bypasses role checks (but not tool checks above)
  if (userRoles.role === "admin") {
    return true;
  }

  // Check if user has required role
  if (item.requiredRole && userRoles.role !== item.requiredRole) {
    return false;
  }

  return true;
};

export const filterNavigationItems = (
  items: NavigationItem[],
  userRoles: UserRoles
): NavigationItem[] => {
  return items.filter((item) => hasAccess(item, userRoles));
};

export const getNavigationMenuItems = (
  userRoles: UserRoles,
  navigate: (path: string) => void
): MenuItemWithHeading[] => {
  const filteredItems = filterNavigationItems(NAVIGATION_ITEMS, userRoles);

  return filteredItems.map((item) => {
    const menuItem: MenuItemWithHeading = {
      label: item.label,
      icon: item.icon,
      heading: item.heading,
      command: () => navigate(item.path),
    };

    // Add submenu items if they exist
    if (item.children && item.children.length > 0) {
      const filteredChildren = filterNavigationItems(item.children, userRoles);
      if (filteredChildren.length > 0) {
        menuItem.items = filteredChildren.map((child) => {
          const childMenuItem: MenuItem = {
            label: child.label,
            icon: child.icon,
            command: () => navigate(child.path),
          };

          // Handle nested children (3rd level)
          if (child.children && child.children.length > 0) {
            const filteredGrandChildren = filterNavigationItems(
              child.children,
              userRoles
            );
            if (filteredGrandChildren.length > 0) {
              childMenuItem.items = filteredGrandChildren.map((grandChild) => ({
                label: grandChild.label,
                icon: grandChild.icon,
                command: () => navigate(grandChild.path),
              }));
            }
          }

          return childMenuItem;
        });
      }
    }

    return menuItem;
  });
};

// Route Path Helpers
export const getRoutePath = (routeKey: keyof typeof ROUTES): string => {
  return ROUTES[routeKey];
};

export const isCurrentRoute = (
  pathname: string,
  routePath: string
): boolean => {
  return pathname === routePath || pathname.startsWith(routePath + "/");
};

// Helper function to easily add new routes
export const addNewRoute = (
  label: string,
  path: string,
  icon: string,
  heading?: string,
  requiredTool?: string,
  requiredRole?: string
): NavigationItem => {
  return {
    label,
    path,
    icon,
    heading,
    requiredTool,
    requiredRole,
  };
};

// Helper function to easily create submenus
export const createSubmenu = (
  parentLabel: string,
  parentPath: string,
  parentIcon: string,
  heading?: string,
  requiredTool?: string,
  requiredRole?: string,
  children: NavigationItem[] = []
): NavigationItem => {
  return {
    label: parentLabel,
    path: parentPath,
    icon: parentIcon,
    heading,
    requiredTool,
    requiredRole,
    children,
  };
};
